import { useState, useEffect } from "react";
import type { PrescriptionItem, OrderDetailsResponse } from "./types";
import { FileImage, Eye, Upload, Loader, X, FileText, Plus, FileEdit } from "lucide-react";
import toast from "react-hot-toast";
import { AdminOrderManagementService } from "../../services/api/admin-order.service";
import GeneratePrescriptionModal from "./GeneratePrescriptionModal";

interface PrescriptionListProps {
  prescriptions: PrescriptionItem[];
  orderId: number;
  orderDetails?: OrderDetailsResponse | null;
  onUploadSuccess?: () => void;
}

const SOURCE_CONFIG = {
  PATIENT_UPLOAD: { label: "Uploaded Prescription (By User)", color: "text-sky-600", badgeBg: "bg-sky-100 text-sky-700" },
  ADMIN_GENERATED: { label: "Uploaded Prescription (By You)", color: "text-purple-600", badgeBg: "bg-purple-100 text-purple-700" },
  DOCTOR_GENERATED: { label: "Generated Prescription", color: "text-emerald-600", badgeBg: "bg-emerald-100 text-emerald-700" },
} as const;

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

interface FilePreviewProps {
  file: File;
  onRemove: () => void;
  disabled?: boolean;
}

const FilePreview: React.FC<FilePreviewProps> = ({ file, onRemove, disabled }) => {
  const [objectUrl, setObjectUrl] = useState<string>("");

  useEffect(() => {
    const isImage = file.type.startsWith("image/");
    let url = "";
    if (isImage) {
      url = URL.createObjectURL(file);
      setObjectUrl(url);
    }
    return () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [file]);

  const isImage = file.type.startsWith("image/");

  return (
    <div className="group relative aspect-video rounded-xl overflow-hidden border border-slate-200 bg-slate-50 flex flex-col justify-center items-center p-3 shadow-sm hover:border-slate-300 transition-all">
      {isImage && objectUrl ? (
        <>
          <img src={objectUrl} alt={file.name} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2 pointer-events-none">
            <span className="text-[0.55rem] text-white truncate w-full font-medium">
              {file.name}
            </span>
          </div>
        </>
      ) : (
        <div className="w-full h-full flex flex-col justify-between items-center text-center">
          <div className="flex-1 flex flex-col justify-center items-center gap-1.5">
            <FileText className="text-rose-500" size={32} />
            <span className="text-[0.65rem] font-semibold text-slate-700 line-clamp-2 max-w-[120px] break-all">
              {file.name}
            </span>
          </div>
          <span className="text-[0.55rem] text-slate-400 font-medium">
            {formatFileSize(file.size)}
          </span>
        </div>
      )}

      {/* Remove button */}
      <button
        type="button"
        className="absolute top-1.5 right-1.5 w-6 h-6 rounded-lg bg-white/90 text-slate-500 hover:text-red-500 hover:bg-red-50 flex items-center justify-center shadow transition-all border border-slate-100 cursor-pointer"
        onClick={onRemove}
        disabled={disabled}
        title="Remove file"
      >
        <X size={12} />
      </button>
    </div>
  );
};

const PrescriptionList: React.FC<PrescriptionListProps> = ({ prescriptions, orderId, orderDetails, onUploadSuccess }) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const newFiles = Array.from(files);
    setSelectedFiles(newFiles);
    setShowModal(true);
    e.target.value = ""; // Reset input value so same file can be selected again
  };

  const handleAddMoreFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const newFiles = Array.from(files);
    setSelectedFiles((prev) => [...prev, ...newFiles]);
    e.target.value = ""; // Reset input value
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCancel = () => {
    setSelectedFiles([]);
    setShowModal(false);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    try {
      setUploading(true);
      const res = await AdminOrderManagementService.uploadPrescription(orderId, selectedFiles);
      if (res.success) {
        toast.success("Prescription uploaded successfully!");
        setSelectedFiles([]);
        setShowModal(false);
        if (onUploadSuccess) onUploadSuccess();
      } else {
        toast.error(res.message || "Failed to upload prescription");
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Failed to upload prescription");
    } finally {
      setUploading(false);
    }
  };

  const grouped = (prescriptions || []).reduce((acc, rx) => {
    const source = rx.source as keyof typeof SOURCE_CONFIG;
    if (!acc[source]) acc[source] = [];
    acc[source].push(rx);
    return acc;
  }, {} as Record<string, PrescriptionItem[]>);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 mb-5 shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
        <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Uploaded Prescriptions</h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowGenerateModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-purple-200 bg-purple-50 text-xs font-semibold text-purple-600 hover:bg-purple-100 transition-all cursor-pointer shadow-sm"
          >
            <FileEdit size={13} />
            Generate Prescription
          </button>
          <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 text-xs font-semibold text-emerald-600 hover:bg-emerald-100 transition-all cursor-pointer shadow-sm">
            {uploading ? <Loader size={13} className="animate-spin" /> : <Upload size={13} />}
            Upload Prescription
            <input
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              multiple
              onChange={handleFileChange}
              disabled={uploading}
            />
          </label>
        </div>
      </div>

      {prescriptions && prescriptions.length > 0 ? (
        <div className="space-y-4">
          {Object.entries(grouped).map(([source, items]) => {
            const config = SOURCE_CONFIG[source as keyof typeof SOURCE_CONFIG];
            if (!config) return null;
            return (
              <div key={source} className="mb-2 last:mb-0">
                <div className={`flex items-center gap-2 text-xs font-bold mb-2 ${config.color}`}>
                  <FileImage size={14} />
                  {config.label}
                </div>
                <div className="flex flex-wrap gap-2">
                  {items.map((rx, i) => (
                    <button
                      key={i}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors cursor-pointer"
                      onClick={() => window.open(rx.media, "_blank")}
                    >
                      <Eye size={12} className="text-slate-400" />
                      {rx.note || `image${i + 1}.jpg`}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-xs text-slate-400 italic py-2">
          No prescriptions uploaded yet.
        </div>
      )}

      {/* Confirmation & Preview Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[1000] flex items-center justify-center animate-fadeIn" onClick={handleCancel}>
          <div className="bg-white rounded-2xl max-w-[640px] w-[95%] max-h-[85vh] flex flex-col shadow-2xl animate-slideUp" onClick={(e) => e.stopPropagation()}>
            
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-sm font-bold text-slate-800">Confirm Prescription Upload</h3>
                <p className="text-[0.65rem] text-slate-400 mt-0.5">Review the selected files before uploading to the order</p>
              </div>
              <button 
                className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors border-none cursor-pointer" 
                onClick={handleCancel}
                disabled={uploading}
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                
                {/* File Previews */}
                {selectedFiles.map((file, idx) => (
                  <FilePreview
                    key={`${file.name}-${idx}`}
                    file={file}
                    onRemove={() => removeFile(idx)}
                    disabled={uploading}
                  />
                ))}

                {/* Add more files card */}
                <label 
                  className="border-2 border-dashed border-slate-200 hover:border-indigo-400 bg-slate-50 hover:bg-indigo-50/20 rounded-xl aspect-video flex flex-col items-center justify-center cursor-pointer transition-all gap-1 shadow-sm group"
                >
                  <Plus size={16} className="text-slate-400 group-hover:text-indigo-500 transition-colors" />
                  <span className="text-[0.65rem] font-bold text-slate-500 group-hover:text-indigo-600 transition-colors">
                    Add More
                  </span>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    multiple
                    onChange={handleAddMoreFiles}
                    disabled={uploading}
                  />
                </label>

              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2.5 bg-slate-50/50 rounded-b-2xl">
              <button
                className="px-4 py-2 rounded-lg text-xs font-semibold border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer disabled:opacity-40"
                onClick={handleCancel}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors border-none cursor-pointer shadow-sm disabled:opacity-50"
                onClick={handleUpload}
                disabled={uploading || selectedFiles.length === 0}
              >
                {uploading ? (
                  <>
                    <Loader size={13} className="animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={13} />
                    Confirm & Upload
                  </>
                )}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Generate Prescription Modal */}
      {showGenerateModal && (
        <GeneratePrescriptionModal
          isOpen={showGenerateModal}
          onClose={() => setShowGenerateModal(false)}
          orderId={orderId}
          orderDetails={orderDetails}
          onSuccess={() => {
            setShowGenerateModal(false);
            if (onUploadSuccess) onUploadSuccess();
          }}
        />
      )}
    </div>
  );
};

export default PrescriptionList;
